import { Store } from "@ngrx/store";
import { inject, Injectable, NgZone, Signal } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { AppState } from "../../state/root";
import { environment } from "../../../../environments/environment";
import { IAlertLock } from "../../../modules/alerts/alert-group";
import { AlertActions, AlertLockActions } from "../../state/alert/alert.actions";
import { IAlert, IAlertComment } from "../../models/alert.model";
import { SoundService } from "../sound/sound.service";
import { selectFilter, selectFilteredAlertsNoWindow, selectIsAlertsPageActive, selectIsFilterLoading, selectPagination, selectPaused, selectSimilarityFilterActive, selectTotalAlerts, selectWindow } from "../../state/alert/alert.selectors";
import { IAlertPagination, IAlerts, IAlertWindow } from "../../state/alert/alert.states";
import { PAGE_SIZE } from "../../constants/filter";
import { IAlertFilter, IAlertOrder } from "../../../modules/alerts/filter";
import { alertMatchesCurrentFilter, alertMatchesDrilldown } from "../../state/alert/helpers/filter.helpers";
import { getMaxPage } from "../../state/alert/helpers/alert.helpers";
import { ILoggedInUser } from "../../models";
import { selectLoggedInUser } from "../../state/user/user.selectors";
import { selectIsAggregateActive, selectDrilldownPath } from "../../../alert-dashboard/store/aggregate.selectors";
import { LogRocketService } from "../log-rocket/log-rocket.service";
import { LocalStorageService } from "../local-storage/local-storage.service";
import { ToastService } from "../toast/toast.service";
import { ToastType } from "../../models";

const UPDATE_ALERT_LOCK_STATE_KEY = 'update_alert_lock_state';
const UPDATE_OTHER_ALERT_LOCK_STATE_KEY = 'update_other_alert_lock_state';
const GET_OTHER_ALERT_LOCKS_KEY = 'get_other_alert_locks';
const GET_ALERT_LOCK_KEY = 'get_alert_lock';
const RELEASE_ALERT_LOCKS = 'release_alert_locks';

class AlertSocketServiceConfig {
    static readonly syncAlertsEventName = 'sync_alerts_response'
    static readonly newAlertEventName = '_new_alert'
    static readonly viewAlertEventName = '_view_alert'
    static readonly dismissAlertEventName = '_dismiss_alert'
    static readonly escalateAlertEventName = '_escalate_alert'
    static readonly muteAlertEventName = '_mute_alert'
    static readonly commentAlertEventName = '_comment_alert'
    static readonly patrolFindingFlaggedEventName = '_patrol_finding_flagged'
}

interface IUpdateAlertSocketMessage {
    data: { alert_id: string, actioned_by: string, timestamp: string, action_timestamp?: string }
}

interface ISyncedSocketEvent {
    type: string;
    payload: unknown;
    id?: number;
}

export interface SocketsAuthenication { sid: string, authenticated: boolean }
// TODO: finish build response type
export interface AlertLockResponse { locked_alerts: unknown[], unlocked_alerts: unknown[] }

export type SocketEvent = "connect" | "reconnect" | "connect_error" | "reconnect_error" | "disconnect" | "error" | "reconnect_attempt" | "ping" | "reconnect_failed" | "_new_alert" | "_dismiss_alert" | "_escalate_alert" | "_comment_alert" | "_patrol_finding_flagged" | "authenticated" | "alert_lock_confirmation" | "confirm_alert_lock" | "alert_lock_state" | "request_alert_lock" | "release_alert_lock" | "poll_alert_lock_state"
const SOCKET_CONNECTION_EVENTS = ['connect', 'reconnect', 'connect_error', 'reconnect_error', 'disconnect', 'reconnect_attempt', 'reconnect_failed'];

function mapSocketAlertLock(locks: { alert_id: string, user_id: string, username: string }[]): IAlertLock[] {
    return locks.map((lock) => {
        return {
            alertID: lock.alert_id,
            userID: lock.user_id,
            username: lock.username
        }
    });
}

// TODO: turn into a common socket service so we don't create multiple socket connections
// Alert Socket Connection
// System Alert Service create a socket connection
@Injectable({
    providedIn: "root"
})
export class AlertSocketService {
    readonly endpoints = {
        socket: `${environment.alertSocketUrl}:${environment.alertSocketPort}`,
    }

    private socketWorker?: Worker | null;

    private store = inject(Store<AppState>)
    private soundService = inject(SoundService);
    private logrocketService = inject(LogRocketService);
    private zone = inject(NgZone);
    private localStorageService = inject(LocalStorageService);
    private toastService = inject(ToastService);

    // TODO: What do we do with the sid
    // NOTE: Remove if auth flow doesn't require sid anymore
    private sid: string | null = null;

    private currentFilteredAlerts: Signal<IAlerts> = this.store.selectSignal(selectFilteredAlertsNoWindow);
    private currentWindowState: Signal<IAlertWindow> = this.store.selectSignal(selectWindow);
    private alertTotal: Signal<number> = this.store.selectSignal(selectTotalAlerts);
    private pagination: Signal<IAlertPagination> = this.store.selectSignal(selectPagination);
    private alertFilter: Signal<IAlertFilter> = this.store.selectSignal(selectFilter);
    private filterLoading: Signal<boolean> = this.store.selectSignal(selectIsFilterLoading);
    private loggedInUser: Signal<ILoggedInUser> = this.store.selectSignal(selectLoggedInUser);
    private isFMPaused: Signal<boolean> = this.store.selectSignal(selectPaused);
    private isAggregateActive: Signal<boolean> = this.store.selectSignal(selectIsAggregateActive);
    private drilldownPath = this.store.selectSignal(selectDrilldownPath);
    private isAlertsPageActive: Signal<boolean> = this.store.selectSignal(selectIsAlertsPageActive);
    private similarityFilterActive: Signal<boolean> = this.store.selectSignal(selectSimilarityFilterActive);
    private loadingAlertFilter = false;
    private pollingIntervalId: ReturnType<typeof setInterval> | null = null;
    private _alertSocketedConnected: Subject<boolean> = new Subject<boolean>();
    private _socketErrored: Subject<boolean> = new Subject<boolean>();
    private _connected = false;
    private lastEventId: number | null = null;
    private processedEventIds: Set<string> = new Set<string>();
    private readonly MAX_CACHE_SIZE = 1000;
    private readonly PATROL_FLAGGED_WINDOW_MS = 60000;
    private patrolFlaggedBuffer: {
        total: number;
        routes: Set<string>;
        sites: Set<string>;
        timer: ReturnType<typeof setTimeout> | null;
    } = { total: 0, routes: new Set(), sites: new Set(), timer: null };


    private initSocketWorker() {
        this.socketWorker = new Worker(new URL('../../web-workers/socket.worker', import.meta.url));
        this.socketWorker.onmessage = ({ data }) => {
            this.zone.run(() => {
                switch (data.type) {
                    case 'CONNECTED': {
                        const connected: boolean = data.state.connected;
                        const connectionType: string = data.state.event;

                        this._connected = connected;
                        this._alertSocketedConnected.next(connected);
                        this.store.dispatch(AlertActions.setSocketConnected({ connected: connected }));

                        this.logSocketConnectionState(connectionType);

                        if (connected && this.lastEventId !== null) {
                            this.emitSocketInWorker('sync_alerts', { last_id: this.lastEventId });
                        }

                        break;
                    }
                    case 'SOCKET_EVENT': {
                        const response = data.args[0];
                        const eventName = data.event;

                        if (eventName === AlertSocketServiceConfig.syncAlertsEventName) {
                            const missedEvents: ISyncedSocketEvent[] = response['events'] || [];
                            const hasValidIds = missedEvents.length > 0 && missedEvents.every(e => typeof e.id === 'number' && e.id > 0);
                            if (missedEvents.length > PAGE_SIZE && hasValidIds) {
                                const maxId = missedEvents.reduce((currentMax, event) => Math.max(currentMax, event.id as number), 0);
                                this.lastEventId = Math.max(this.lastEventId || 0, maxId);
                                if (this.isAlertsPageActive()) {
                                    this.store.dispatch(AlertActions.loadAlerts({ showLoading: false }));
                                }
                                break;
                            }
                            missedEvents.forEach(event => {
                                const mappedEventName = event.type.includes(this.loggedInUser().organisation_id)
                                    ? event.type
                                    : `${this.loggedInUser().organisation_id}${event.type}`;

                                this.processIncomingAlert(mappedEventName, event.payload, event.id);
                            });
                            break; // Stop here, we processed the bulk array
                        }

                        // Handle standard live events
                        this.processIncomingAlert(eventName, response, response.socket_msg_id);
                        break;
                    }
                    default: {
                        console.warn(`This event is not catered for`);
                        console.warn(data)
                    }
                }
            });
        };
    }

    private routeSocketEvent(eventName: string, response: unknown): void {
        const organisationId = this.loggedInUser().organisation_id;
        const userId = this.loggedInUser().id;

        const handlers: Record<string, () => void> = {
            [UPDATE_ALERT_LOCK_STATE_KEY]: () => {
                const lockResponse = response as { alert_locks: { alert_id: string, user_id: string, username: string }[], group_index: number };
                const alertLocks = mapSocketAlertLock(lockResponse.alert_locks);
                this.store.dispatch(AlertLockActions.setLocks({ locks: alertLocks, userId, groupIndex: lockResponse.group_index }));
            },
            [UPDATE_OTHER_ALERT_LOCK_STATE_KEY]: () => {
                this.store.dispatch(AlertLockActions.setOtherLocks({ locks: mapSocketAlertLock(response as { alert_id: string, user_id: string, username: string }[]), userId }));
            },
            [`${organisationId}${AlertSocketServiceConfig.dismissAlertEventName}`]: () => this.dismissAlertOnSocketEvent(response as IUpdateAlertSocketMessage),
            [`${organisationId}${AlertSocketServiceConfig.viewAlertEventName}`]: () => this.viewAlertOnSocketEvent(response as IUpdateAlertSocketMessage),
            [`${organisationId}${AlertSocketServiceConfig.newAlertEventName}`]: () => this.newAlertOnSocketEvent(response),
            [`${organisationId}${AlertSocketServiceConfig.escalateAlertEventName}`]: () => this.escalateAlertOnSocketEvent(response as IUpdateAlertSocketMessage),
            [`${organisationId}${AlertSocketServiceConfig.muteAlertEventName}`]: () => this.muteAlertOnSocketEvent(response as IUpdateAlertSocketMessage),
            [`${organisationId}${AlertSocketServiceConfig.commentAlertEventName}`]: () => this.commentAlertonSocketEvent(response),
            [`${organisationId}${AlertSocketServiceConfig.patrolFindingFlaggedEventName}`]: () => this.patrolFindingFlaggedOnSocketEvent(response),
            ['authenticated']: () => this.socketConnectionAuth(response as SocketsAuthenication),
        };

        const handler = handlers[eventName];
        if (handler) {
            handler();
        } else {
            console.warn('Socket event not catered for', eventName, response);
        }
    }

    // NOTE: worker functions
    initSocketConnectionInWorker(url: string, options?: unknown) {
        if (this.socketWorker != null) {
            this.socketWorker.postMessage({ type: 'INIT', payload: { url, options } });
        }
    }

    emitSocketInWorker(event: string, data: unknown) {
        if (this.socketWorker != null) {
            this.socketWorker.postMessage({ type: 'EMIT', payload: { event, data } });
        }
    }

    disconnectSocketInWorker() {
        if (this.socketWorker != null) {
            this.socketWorker.postMessage({ type: 'DISCONNECT' });
        }
    }
    // END NOTE

    initialise() {
        this.initSocketWorker();
        // NOTE: Passing in options because we do not have access to localStorage inside the web worker
        const options = this.getSocketOptions();
        this.initSocketConnectionInWorker(this.endpoints.socket, options);
        this.startPolling();
    }

    destroy() {
        this.disconnectSocketInWorker();
        if (this.socketWorker != null) {
            this.socketWorker.terminate();
        }
        this.stopPolling();
    }

    private getSocketOptions() {
        const token = this.localStorageService.getItem(environment.authTokenKey);

        if (token == null) {
            console.error('Socket connection, invalid token');
        }

        return {
            'reconnectionDelayMax': 10000,
            'reconnectionDelay': 10000,
            transportOptions: {
                polling: {
                    extraHeaders: { "Authorization": token },
                },
            }
        }
    }

    private socketConnectionAuth(data: SocketsAuthenication) {
        if (data == null || !data.authenticated) {
            console.error('Socket Service: user not authenticated');
            // NOTE: Disabling so socket doesn't route user to session expired page
            // this.store.dispatch(UserActions.sessionExpired());
            return;
        }

        this.sid = data.sid;
    }

    private logSocketConnectionState(connectionType: string) {
        // NOTE: Log Rocket Logging
        if (SOCKET_CONNECTION_EVENTS.includes(connectionType)) {
            const lRTrackData = {
                'alertsItemLength': this.currentFilteredAlerts().items.length,
                'currentTotal': this.currentFilteredAlerts().total
            };

            this.logrocketService.customTrack(`socket_${connectionType}`, lRTrackData);
        }
    }

    isConnected(): Observable<boolean> {
        return this._alertSocketedConnected.asObservable();
    }

    socketErrorState(): Observable<boolean> {
        return this._socketErrored.asObservable();
    }

    requestAlertLocks(request: unknown) {
        this.emitSocketInWorker(GET_ALERT_LOCK_KEY, request);
    }

    requestOtherLocks(request: unknown) {
        this.emitSocketInWorker(GET_OTHER_ALERT_LOCKS_KEY, request);
    }

    private verifyAlertStack() {
        if (!this.isAlertsPageActive()) {
            return;
        }
        if (this.filterLoading()) {
            return;
        }

        const total = this.alertTotal();
        const currentWindow = this.currentWindowState();
        if (total === 0 && currentWindow.alerts.length === 0) {
            // Release locks if there are no alerts and no active window group
            this.store.dispatch(AlertLockActions.releaseLocks({ ids: currentWindow.alertIdsToRelease }));
            this.store.dispatch(AlertActions.resetWindowState());
        }

        if (this.loadingAlertFilter) {
            return;
        }

        const count = this.currentFilteredAlerts().items.length;
        const currentPage = this.pagination().current;
        const maxPage = getMaxPage(total);

        const minExpected = currentPage * PAGE_SIZE;
        const maxExpected = (currentPage + 1) * PAGE_SIZE;
        const isAtMaxPage = currentPage === maxPage;
        const isPastLastPage = currentPage > maxPage;

        let isOutOfSync = count < minExpected || count > maxExpected || isPastLastPage;
        if (isAtMaxPage) {
            isOutOfSync = total !== count;
        }

        if (!isOutOfSync) {
            return;
        }

        this.loadAlertPage(1);
    }

    private loadAlertPage(page: number) {
        this.store.dispatch(AlertActions.loadAlertPage({ page }));
        this.loadingAlertFilter = true;
        setTimeout(() => {
            this.loadingAlertFilter = false;
        }, 30000); // 30 seconds so it doesn't spam filter
    }

    private startPolling() {
        this.stopPolling();
        this.pollingIntervalId = setInterval(() => {
            if (!this.isAlertsPageActive() || this.filterLoading() || this.loadingAlertFilter || this.alertTotal() > PAGE_SIZE) {
                return;
            }

            this.loadAlertPage(1);
        }, 60000); // Poll every 60 seconds
    }

    private stopPolling() {
        if (this.pollingIntervalId == null) {
            return;
        }
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
    }

    private processIncomingAlert(eventName: string, payload: unknown, monotonicId?: number): void {
        const payloadRecord = payload as Record<string, unknown> | null | undefined;
        const actualData = (payloadRecord?.['data'] as Record<string, unknown> | null | undefined) || payloadRecord;
        const alertId = actualData?.['alert_id'] || actualData?.['id'];
        // can still receive different action types while deduping repeated identical actions.
        const eventType = eventName.split('_').slice(-2).join('_');
        const uniqueDiscriminator = monotonicId ?? actualData?.['comment_id'] ?? null;
        const dedupeKey = alertId
            ? (uniqueDiscriminator != null ? `${eventType}-${alertId}-${uniqueDiscriminator}` : `${eventType}-${alertId}`)
            : null;

        if (dedupeKey != null) {
            if (this.processedEventIds.has(dedupeKey)) {
                return; // Already processed this event, silently drop it.
            }

            this.processedEventIds.add(dedupeKey);
            if (this.processedEventIds.size > this.MAX_CACHE_SIZE) {
                const oldestKey = this.processedEventIds.values().next().value;
                if (oldestKey != null) {
                    this.processedEventIds.delete(oldestKey);
                }
            }
        }

        // Advance the Monotonic Cursor
        if (monotonicId != null) {
            this.lastEventId = Math.max(this.lastEventId || 0, monotonicId);
        }

        const ALERT_MUTATION_SUFFIXES = [
            AlertSocketServiceConfig.newAlertEventName,
            AlertSocketServiceConfig.viewAlertEventName,
            AlertSocketServiceConfig.dismissAlertEventName,
            AlertSocketServiceConfig.escalateAlertEventName,
            AlertSocketServiceConfig.muteAlertEventName,
            AlertSocketServiceConfig.commentAlertEventName,
            AlertSocketServiceConfig.patrolFindingFlaggedEventName,
        ];
        if (this.similarityFilterActive() && ALERT_MUTATION_SUFFIXES.some(suffix => eventName.endsWith(suffix))) {
            return;
        }

        this.routeSocketEvent(eventName, payload);
    }

    private newAlertWithinValidPagination(): boolean {
        const filterOrdering: IAlertOrder[] | undefined = this.alertFilter().ordering;
        if (filterOrdering == null) {
          return true;
        }

        const filterKey: string = filterOrdering[0].key;
        const filterDirection: "ASC" | "DESC" = filterOrdering[0].direction;

        if (filterKey !== "creation_timestamp") {
          return true;
        }
        if (filterDirection === "DESC") {
          return true;
        }
        if (this.pagination().current >= Math.floor(this.alertTotal() / PAGE_SIZE)) {
          return true;
        }
        return false;
    }

    private cleanNewAlertData(newAlert: Record<string, unknown>): Record<string, unknown> {
        delete newAlert['frame'];
        delete newAlert['motion_zones'];
        delete newAlert['frame_track'];
        delete newAlert['action'];
        return newAlert;
    }

    // TODO: Add new action that focuses on adding new alerts
    private newAlertOnSocketEvent(data: unknown) {
        const alertFilter = this.alertFilter();
        let newAlert: Record<string, unknown> = {
            ...(data as Record<string, unknown>)['data'] as Record<string, unknown>,
            comments: []
        };
        newAlert = this.cleanNewAlertData(newAlert);

        // NOTE: Adding creation_timestamp because alerts from sockets don't have creation_timestamp
        newAlert['creation_timestamp'] = newAlert['detection_time'];
        newAlert['id'] = newAlert['id'] != undefined ? newAlert['id'] : newAlert['alert_id'];

        // Getting the rule name
        newAlert['rule_name'] = this.getRuleNameFromSocketAlert(newAlert);
        newAlert['dismissed'] = false;
        if (["high", "severe"].includes(`${newAlert["priority"]}`.toLowerCase())) {
            this.soundService.play();
        }

        const alert = newAlert as unknown as IAlert;
        const alertMatchesFilter: boolean = alertMatchesCurrentFilter(alert, alertFilter);
        const drilldownPath = this.drilldownPath();
        const alertMatchesDrilldownPath: boolean = !this.isAggregateActive() || drilldownPath.length === 0
            || alertMatchesDrilldown(alert, drilldownPath);
        if (this.newAlertWithinValidPagination() && alertMatchesFilter && alertMatchesDrilldownPath) {
            this.store.dispatch(AlertActions.addAlert({ alert }));

            const canAttemptAcquireLock: boolean = this.currentWindowState().canAcquireLocks && !this.isFMPaused();
            if (this.currentWindowState().alerts.length < 1 && canAttemptAcquireLock) {
                this.store.dispatch(AlertLockActions.acquireNewLocks());
            }

        } else if (alertMatchesFilter && alertMatchesDrilldownPath) {
            this.store.dispatch(AlertActions.incrementAlertTotal());
        }

        this.verifyAlertStack();
    }

    // TODO: Add new action that focuses on updating viewed alerts
    private viewAlertOnSocketEvent(data: IUpdateAlertSocketMessage) {
        if (data == null) {
            console.error('Got view event with invalid data');
            return;
        }

        this.store.dispatch(AlertActions.updateAlert({
            alert: {
                id: data.data.alert_id,
                viewed: true,
                viewed_by: data.data.actioned_by,
                view_timestamp: data.data.timestamp
            }
        }));
        this.store.dispatch(AlertActions.appendAlertAction({
            alertId: data.data.alert_id,
            action: { id: crypto.randomUUID(), alert_id: data.data.alert_id, action_type: 'view', action_by: data.data.actioned_by, action_timestamp: data.data.action_timestamp ?? data.data.timestamp }
        }));

        this.verifyAlertStack();
    }

    // TODO: Add new action that focuses on deleting alerts
    private dismissAlertOnSocketEvent(data: IUpdateAlertSocketMessage) {
        if (data == null) {
            console.error('Got dismiss event with invalid data');
            return;
        }

        this.store.dispatch(AlertActions.updateAlert({
            alert: {
                id: data.data.alert_id,
                dismissed: true,
                dismissed_by: data.data.actioned_by,
                dismiss_timestamp: data.data.timestamp
            }
        }));
        this.store.dispatch(AlertActions.appendAlertAction({
            alertId: data.data.alert_id,
            action: { id: crypto.randomUUID(), alert_id: data.data.alert_id, action_type: 'dismiss', action_by: data.data.actioned_by, action_timestamp: data.data.action_timestamp ?? data.data.timestamp }
        }));

        this.verifyAlertStack();
    }

    // TODO: Add new action that focuses on escaltaing alerts
    private escalateAlertOnSocketEvent(data: IUpdateAlertSocketMessage) {
        this.store.dispatch(AlertActions.updateAlert({
            alert: {
                id: data.data.alert_id,
                escalated: true,
                escalated_by: data.data.actioned_by,
                escalation_timestamp: data.data.timestamp
            }
        }));
        this.store.dispatch(AlertActions.appendAlertAction({
            alertId: data.data.alert_id,
            action: { id: crypto.randomUUID(), alert_id: data.data.alert_id, action_type: 'escalate', action_by: data.data.actioned_by, action_timestamp: data.data.action_timestamp ?? data.data.timestamp }
        }));

        this.verifyAlertStack();
    }

    // TODO: Add new Action taht focuses on muting alerts
    private muteAlertOnSocketEvent(data: IUpdateAlertSocketMessage) {
        this.store.dispatch(AlertActions.updateAlert({
            alert: {
                id: data.data.alert_id,
                muted: true,
                muted_by: data.data.actioned_by,
                mute_timestamp: data.data.timestamp
            }
        }));
        this.store.dispatch(AlertActions.appendAlertAction({
            alertId: data.data.alert_id,
            action: { id: crypto.randomUUID(), alert_id: data.data.alert_id, action_type: 'mute', action_by: data.data.actioned_by, action_timestamp: data.data.action_timestamp ?? data.data.timestamp }
        }));

        this.verifyAlertStack();
    }

    private commentAlertonSocketEvent(data: unknown) {
        this.store.dispatch(AlertActions.addComment({ comment: (data as Record<string, unknown>)["data"] as IAlertComment }))
        this.verifyAlertStack();
    }

    private patrolFindingFlaggedOnSocketEvent(data: unknown) {
        const dataRecord = data as Record<string, unknown> | null | undefined;
        const payload = (dataRecord?.['data'] as Record<string, unknown> | null | undefined) ?? dataRecord;
        const routeName: string = (payload?.['route_name'] as string | undefined) ?? 'Unknown Route';
        const siteName: string = (payload?.['site_name'] as string | undefined) ?? 'Unknown Site';
        const flaggedCount: number = (payload?.['flagged_count'] as number | undefined) ?? 0;

        if (flaggedCount <= 0) {
            return;
        }

        this.patrolFlaggedBuffer.total += flaggedCount;
        this.patrolFlaggedBuffer.routes.add(routeName);
        this.patrolFlaggedBuffer.sites.add(siteName);

        if (this.patrolFlaggedBuffer.timer == null) {
            this.patrolFlaggedBuffer.timer = setTimeout(() => {
                this.flushPatrolFlaggedBuffer();
            }, this.PATROL_FLAGGED_WINDOW_MS);
        }
    }

    private flushPatrolFlaggedBuffer(): void {
        const { total, routes } = this.patrolFlaggedBuffer;
        if (total <= 0) {
            return;
        }

        const routeList = Array.from(routes).slice(0, 3).join(', ');
        const moreRoutes = routes.size > 3 ? ` and ${routes.size - 3} more` : '';
        const message = `Scout Patrol Flagged Issues: ${total} issue${total > 1 ? 's' : ''} flagged${routeList ? ` across ${routeList}${moreRoutes}` : ''}`;

        this.toastService.showToast(message, ToastType.WARNING, 8000, 'patrol-flagged');
        this.patrolFlaggedBuffer = { total: 0, routes: new Set(), sites: new Set(), timer: null };
    }

    releaseNewAlertLocks(alertIDs: string[]) {
        if (!this._connected) {
            console.warn('Socket not connected, cannot release locks')
            return;
        }

        this.emitSocketInWorker(RELEASE_ALERT_LOCKS, alertIDs);
    }

    private getRuleNameFromSocketAlert(newSocketAlert: Record<string, unknown>): string {
        const rules = (newSocketAlert['rules'] as { broken?: boolean, rule_name?: string }[] | undefined) ?? [];
        for (const rule of rules) {
            if (rule.broken ?? false) {
                return rule.rule_name ?? ""
            }
        }
        return ""
    }
}
